/**
 * «Asignación inicial» — the names of stores and articles (usability audit B1–B3, DEC-111): «Santafé 1203», never
 * the key alone where a name exists.
 *
 * **Where the names come from.** The run itself, by key (`GET /launch/initial/runs/{run_id}/names`, the same
 * model and the same rule as the in-season names read): the store master in force and the article attributes in
 * force on the run's date, each key asked once, up to {@link NAMES_PER_REQUEST} of a kind in a request. A key the
 * platform cannot name keeps its code and carries the platform's own reason.
 *
 * This is the launch chapter's own reader, not the in-season hook (`useRunNames`): a feature does not import
 * another's modules, and that hook reads the in-season run's reports. The model is the same, so a name reads the
 * same wherever the key is drawn.
 *
 * **The keys a page draws, asked for by the page** — it holds its rows, so it lists their keys; no key is walked
 * for. Until the answer arrives a key reads as «reading» and shows its code; a request that fails leaves its keys as
 * their codes. Every request is fresh for the session (a name is a property of the run’s key) and a
 * `BACKGROUND_READ`: nothing waits on it.
 *
 * Files read the same answer outside React (`readNames`) for the keys of the rows they hold.
 */

import { useMemo } from 'react';
import type { TFunction } from 'i18next';

import { BACKGROUND_READ, callOperation, SESSION_LONG_READ, useApiQueries } from '@/api';
import { readRunNames, type RunNamesResponse } from '@/api/generated';

/** The most keys of one kind the names read takes in a request. */
export const NAMES_PER_REQUEST = 100;

/** One key as a page shows it: its code, and its name or why there is none. */
export interface Named {
  readonly code: string;
  readonly name: string | null;
  readonly reason: string | null;
}

export type NamesState = 'reading' | 'ready' | 'unreadable';

/** The lookups a page holds. */
export interface Names {
  readonly state: NamesState;
  readonly store: (code: string) => Named;
  readonly article: (code: string) => Named;
}

interface Entry { readonly name: string | null; readonly reason: string | null }

/** The names of nothing: every key is its code. */
export const NO_NAMES: Names = {
  state: 'unreadable',
  store: (code) => ({ code, name: null, reason: null }),
  article: (code) => ({ code, name: null, reason: null }),
};

/** `keys`, each once, in a stable order, so a request is the same request on every render. Pure. */
export function distinctKeys(keys: Iterable<string | null | undefined>): readonly string[] {
  return [...new Set([...keys].filter((key): key is string => typeof key === 'string' && key !== ''))].sort();
}

/** `keys` cut into requests of {@link NAMES_PER_REQUEST} of each kind. Pure. */
export function nameChunks(stores: readonly string[], articles: readonly string[]): readonly { readonly stores: readonly string[]; readonly articles: readonly string[] }[] {
  const count = Math.max(Math.ceil(stores.length / NAMES_PER_REQUEST), Math.ceil(articles.length / NAMES_PER_REQUEST));
  return Array.from({ length: count }, (_, index) => ({
    stores: stores.slice(index * NAMES_PER_REQUEST, (index + 1) * NAMES_PER_REQUEST),
    articles: articles.slice(index * NAMES_PER_REQUEST, (index + 1) * NAMES_PER_REQUEST),
  }));
}

/** The names an answer carries, as lookups over `state`. A name beats an absence. Pure. */
export function namesOf(answers: readonly Pick<RunNamesResponse, 'stores' | 'articles'>[], state: NamesState): Names {
  const stores = new Map<string, Entry>();
  const articles = new Map<string, Entry>();
  const put = (map: Map<string, Entry>, key: string, name: string | null | undefined, reason: string | null | undefined): void => {
    const known = map.get(key);
    if (known?.name !== undefined && known.name !== null) return;
    if (typeof name === 'string' && name !== '') map.set(key, { name, reason: null });
    else if (known === undefined) map.set(key, { name: null, reason: reason ?? null });
  };
  for (const answer of answers) {
    for (const row of answer.stores ?? []) put(stores, row.store_key, row.store_name, row.store_name_absent_reason);
    for (const row of answer.articles ?? []) put(articles, row.article_key, row.article_name, row.article_name_absent_reason);
  }
  const lookup = (map: Map<string, Entry>) => (code: string): Named => ({ code, name: map.get(code)?.name ?? null, reason: map.get(code)?.reason ?? null });
  return { state, store: lookup(stores), article: lookup(articles) };
}

/** The names of the keys a page holds. `runId` `null`, or no key, reads nothing. */
export function useNames(runId: string | null, storeKeys: readonly string[], articleKeys: readonly string[]): Names {
  const stores = useMemo(() => distinctKeys(storeKeys), [storeKeys]);
  const articles = useMemo(() => distinctKeys(articleKeys), [articleKeys]);
  const chunks = useMemo(() => nameChunks(stores, articles), [stores, articles]);
  const answers = useApiQueries(
    readRunNames,
    chunks.map((chunk) => ({
      path: { run_id: runId ?? '' },
      query: {
        ...(chunk.stores.length === 0 ? {} : { store_key: [...chunk.stores] }),
        ...(chunk.articles.length === 0 ? {} : { article_key: [...chunk.articles] }),
      },
    })),
    { enabled: runId !== null, ...SESSION_LONG_READ, ...BACKGROUND_READ },
  );
  const pending = answers.some((read) => read.isPending);
  const failed = answers.some((read) => read.isError);
  const signature = answers.map((read) => `${read.status}:${read.dataUpdatedAt}`).join('|');
  return useMemo(() => {
    if (runId === null) return NO_NAMES;
    return namesOf(answers.flatMap((read) => (read.data === undefined ? [] : [read.data])), pending ? 'reading' : failed ? 'unreadable' : 'ready');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` stands for the answers' data
  }, [runId, pending, failed, signature]);
}

/**
 * The same names outside React, for a file: every key asked in chunks, together. A read that fails names nothing
 * (the file keeps its codes), never rejects.
 */
export async function readNames(runId: string, storeKeys: readonly string[], articleKeys: readonly string[]): Promise<Names> {
  const chunks = nameChunks(distinctKeys(storeKeys), distinctKeys(articleKeys));
  try {
    const answers = await Promise.all(chunks.map((chunk) => callOperation(readRunNames, {
      path: { run_id: runId },
      query: {
        ...(chunk.stores.length === 0 ? {} : { store_key: [...chunk.stores] }),
        ...(chunk.articles.length === 0 ? {} : { article_key: [...chunk.articles] }),
      },
    })));
    return namesOf(answers, 'ready');
  } catch {
    return NO_NAMES;
  }
}

/** «Santafé (1203)» where named, the code alone otherwise — for sentences, lists and labels. */
export function withCode(t: TFunction, named: Named): string {
  return named.name === null ? named.code : t('launch.initial.names.withCode', { name: named.name, code: named.code });
}

/** A key in a cell: the name first, the code beside it in the numeric face; the code alone where there is no name. */
export function NameCell({ named }: { readonly named: Named }): React.JSX.Element {
  if (named.name === null) return <span className="font-numeric">{named.code}</span>;
  return (
    <span className="inline-flex items-baseline gap-2">
      <span>{named.name}</span>
      <span className="font-numeric text-ink-secondary text-[12px]">{named.code}</span>
    </span>
  );
}
